import { exact, multiplyExact, percent, roundToMinor } from '../pricing/exact.js';
/**
 * Line tax. Appendix D `MR-03` step 9, `MR-07`.
 *
 * > `MR-07` Tax-inclusive markets compute the tax component **by extraction, not by addition**, and
 * > **the displayed price is the authority**.
 *
 * Both halves of that sentence matter and the second is the one that gets lost.
 *
 * In an inclusive market a shelf price of 1,000 **is** 1,000. The tax inside it is
 * `1000 * rate / (1 + rate)`, and the net is what is left. Computing it by addition instead, taking
 * some net and adding tax, produces a gross that differs from the displayed price by a minor unit
 * often enough to matter, and then the shop is charging a number that is not on the label. In
 * Pakistan that is also a compliance problem rather than only an embarrassing one.
 *
 * So the inclusive path derives net **from** gross and never the other way round, and the gross it
 * was given comes back unchanged.
 */
export class TaxError extends Error {
}
export const TAX_TREATMENTS = ['inclusive', 'exclusive', 'exempt', 'zero_rated'];
/**
 * Compute the tax on one line.
 *
 * `amountMinor` is read according to the treatment: it is the **gross** for an inclusive line and
 * the **net** for an exclusive one. That asymmetry is `MR-07`'s, not an interface convenience: in an
 * inclusive market the displayed price is the authority, so the number the shop typed is the gross.
 */
export function taxLine(amountMinor, rate, rounding) {
    if (rate.treatment === 'exempt' || rate.treatment === 'zero_rated') {
        // Different reasons, same arithmetic, and both are kept because an invoice and a return have to
        // distinguish them: zero-rated is taxable at 0% and exempt is outside the tax entirely.
        return { grossMinor: amountMinor, netMinor: amountMinor, taxMinor: 0n, rate };
    }
    const rateExact = percent(rate.percent);
    if (rate.treatment === 'exclusive') {
        // Net given, tax added. One rounding, at the end, per MR-04.
        const taxMinor = roundToMinor(multiplyExact(exact(amountMinor), rateExact), rounding);
        return {
            grossMinor: amountMinor + taxMinor,
            netMinor: amountMinor,
            taxMinor,
            rate,
        };
    }
    // Inclusive. tax = gross * rate / (1 + rate), exactly, then rounded once.
    //
    // The net is then gross - tax rather than a second computation. Computing both independently and
    // rounding each lets net + tax differ from gross by a unit, and the invoice would then not add up.
    const denominator = exact(rateExact.d + rateExact.n, rateExact.d);
    const taxExact = multiplyExact(exact(amountMinor), exact(rateExact.n * denominator.d, rateExact.d * denominator.n));
    const taxMinor = roundToMinor(taxExact, rounding);
    return {
        grossMinor: amountMinor,
        netMinor: amountMinor - taxMinor,
        taxMinor,
        rate,
    };
}
/**
 * `MR-07`'s property, exposed so a caller can assert it at a boundary.
 *
 * An invoice whose net and tax do not add to its gross is one a tax authority rejects and a customer
 * queries, and it is the shape that appears when the two are computed independently.
 */
export function taxAddsUp(taxed) {
    return taxed.netMinor + taxed.taxMinor === taxed.grossMinor;
}
/**
 * Total a set of taxed lines, keeping the components separate.
 *
 * Summed from the already-rounded line figures rather than recomputed from the order total. `MR-03`
 * puts tax at step 9, per line, and a total recomputed at order level can differ from the sum of
 * the lines it is printed above.
 */
export function totalTax(lines) {
    return lines.reduce((sum, line) => ({
        grossMinor: sum.grossMinor + line.grossMinor,
        netMinor: sum.netMinor + line.netMinor,
        taxMinor: sum.taxMinor + line.taxMinor,
    }), { grossMinor: 0n, netMinor: 0n, taxMinor: 0n });
}
//# sourceMappingURL=tax.js.map